import { useEffect, useState, useCallback, useMemo } from "react";
import { UserCheck, RefreshCw, KeyRound, Clock, User, Phone, CheckCircle } from "lucide-react";
import { toast } from "react-toastify";
import { getStoredAuthUser } from "../../../services/authService";
import { getExpectedInvites, checkInPreApprovedInvite } from "../../../services/inviteService";
import AdminLayout from "../../../layouts/AdminLayout";
import {
  DataTable,
  SearchInput,
  Button,
  StatusBadge,
  Modal,
} from "../../../components/ui";

const GuardExpectedVisitors = () => {
  const [loading, setLoading] = useState(true);
  const [expectedInvites, setExpectedInvites] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [confirmModalData, setConfirmModalData] = useState(null);
  const [checkingIn, setCheckingIn] = useState(false);

  const authUser = getStoredAuthUser();
  const societyId = authUser?.society_id;

  const loadExpectedInvites = useCallback(async () => {
    if (!societyId) return;
    setLoading(true);
    try {
      const invitesData = await getExpectedInvites(societyId);
      setExpectedInvites(
        Array.isArray(invitesData) ? invitesData : invitesData?.invites || []
      );
    } catch (error) {
      console.error("LOAD EXPECTED INVITES ERROR:", error);
      toast.error("Unable to load expected visitors");
    } finally {
      setLoading(false);
    }
  }, [societyId]);

  useEffect(() => {
    loadExpectedInvites();
  }, [loadExpectedInvites]);

  const handleConfirmCheckIn = async () => {
    if (!confirmModalData) return;
    setCheckingIn(true);
    try {
      await checkInPreApprovedInvite(confirmModalData.id);
      toast.success(
        `${confirmModalData.visitor_name || "Guest"} verified & checked in!`
      );
      setConfirmModalData(null);
      await loadExpectedInvites();
    } catch (error) {
      console.error("INVITE CHECK IN ERROR:", error);
      toast.error("Unable to check in guest. Please verify pass validity.");
    } finally {
      setCheckingIn(false);
    }
  };

  const filteredInvites = useMemo(() => {
    if (!searchQuery.trim()) return expectedInvites;
    const q = searchQuery.toLowerCase();
    return expectedInvites.filter((item) => {
      const name = (item.visitor_name || "").toLowerCase();
      const phone = (item.mobile_number || "").toLowerCase();
      const pass = (item.pass_code || "").toLowerCase();
      const host = (item.host_name || "").toLowerCase();
      return name.includes(q) || phone.includes(q) || pass.includes(q) || host.includes(q);
    });
  }, [expectedInvites, searchQuery]);

  const columns = [
    {
      key: "visitor_name",
      label: "Visitor Details",
      sortable: true,
      render: (val, row) => (
        <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
          <span style={{ fontWeight: 700, color: "var(--color-text-strong)" }}>
            {val || "Guest"}
          </span>
          {row.mobile_number && (
            <span
              style={{
                fontSize: "0.775rem",
                color: "var(--color-muted)",
                display: "inline-flex",
                alignItems: "center",
                gap: "4px",
              }}
            >
              <Phone size={12} /> {row.mobile_number}
            </span>
          )}
        </div>
      ),
    },
    {
      key: "invite_type",
      label: "Category",
      sortable: true,
      render: (val) => (
        <StatusBadge
          status="INFO"
          label={val || "Guest Pass"}
          showDot={false}
        />
      ),
    },
    {
      key: "pass_code",
      label: "Pass Code",
      sortable: true,
      render: (val) => (
        <span
          style={{
            fontFamily: "var(--font-heading)",
            fontWeight: 800,
            letterSpacing: "0.06em",
            color: "var(--color-dark-bg)",
            background: "rgba(39, 48, 111, 0.08)",
            padding: "3px 8px",
            borderRadius: "6px",
            display: "inline-flex",
            alignItems: "center",
            gap: "5px",
          }}
        >
          <KeyRound size={13} /> {val || "—"}
        </span>
      ),
    },
    {
      key: "valid_from",
      label: "Validity Window",
      sortable: true,
      render: (val, row) => (
        <div style={{ fontSize: "0.8rem", color: "var(--color-text-secondary)" }}>
          <div style={{ display: "inline-flex", alignItems: "center", gap: "4px" }}>
            <Clock size={12} />
            {val ? new Date(val).toLocaleDateString() : "Always Active"}
          </div>
          {row.valid_until && (
            <div style={{ fontSize: "0.75rem", color: "var(--color-muted)" }}>
              Until: {new Date(row.valid_until).toLocaleDateString()}
            </div>
          )}
        </div>
      ),
    },
    {
      key: "host_name",
      label: "Host / Destination",
      render: (val, row) => (
        <div style={{ fontSize: "0.825rem", color: "var(--color-text)" }}>
          <div style={{ fontWeight: 600 }}>{val || "Property Resident"}</div>
          <div style={{ fontSize: "0.75rem", color: "var(--color-muted)" }}>
            {row.unit_name || row.flat_number || row.department_name || "Assigned Unit"}
          </div>
        </div>
      ),
    },
    {
      key: "actions",
      label: "Gate Action",
      render: (_, row) => (
        <Button
          variant="primary"
          size="sm"
          onClick={() => setConfirmModalData(row)}
          icon={<UserCheck size={14} />}
        >
          Verify & Check In
        </Button>
      ),
    },
  ];

  return (
    <AdminLayout
      title="Expected Visitors"
      actions={
        <Button
          variant="outline"
          size="sm"
          onClick={loadExpectedInvites}
          loading={loading}
          icon={<RefreshCw size={14} />}
        >
          Refresh List
        </Button>
      }
    >
      <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
        {/* Header Summary Bar */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            background: "var(--color-surface)",
            border: "1px solid var(--color-border)",
            borderRadius: "12px",
            padding: "16px 20px",
            boxShadow: "var(--shadow-xs)",
            flexWrap: "wrap",
            gap: "12px",
          }}
        >
          <div>
            <h2
              style={{
                fontFamily: "var(--font-heading)",
                fontSize: "1.2rem",
                fontWeight: 700,
                color: "var(--color-text-strong)",
                margin: "0 0 4px",
              }}
            >
              Pre-Approved Guest Passes
            </h2>
            <p
              style={{
                fontSize: "0.85rem",
                color: "var(--color-muted)",
                margin: 0,
              }}
            >
              Visitors pre-registered by hosts and residents awaiting physical gate arrival.
            </p>
          </div>

          <SearchInput
            placeholder="Search by name, phone, pass code..."
            value={searchQuery}
            onChange={(val) => setSearchQuery(val)}
          />
        </div>

        {/* Universal DataTable */}
        <DataTable
          columns={columns}
          data={filteredInvites}
          isLoading={loading}
          emptyMessage="No pre-approved expected visitors found."
          rowKey="id"
        />

        {/* Check-In Confirmation Modal */}
        <Modal
          isOpen={!!confirmModalData}
          onClose={() => !checkingIn && setConfirmModalData(null)}
          title="Verify & Check In Visitor"
          size="sm"
          footer={
            <>
              <Button
                variant="outline"
                onClick={() => setConfirmModalData(null)}
                disabled={checkingIn}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleConfirmCheckIn}
                loading={checkingIn}
                icon={<CheckCircle size={15} />}
              >
                Confirm Gate Check-In
              </Button>
            </>
          }
        >
          {confirmModalData && (
            <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                  background: "var(--color-light-bg)",
                  border: "1px solid var(--color-border)",
                  borderRadius: "10px",
                  padding: "14px",
                }}
              >
                <div
                  style={{
                    width: "44px",
                    height: "44px",
                    borderRadius: "50%",
                    background: "rgba(39, 48, 111, 0.1)",
                    color: "var(--color-dark-bg)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <User size={22} />
                </div>
                <div>
                  <div
                    style={{
                      fontFamily: "var(--font-heading)",
                      fontSize: "1rem",
                      fontWeight: 700,
                      color: "var(--color-text-strong)",
                    }}
                  >
                    {confirmModalData.visitor_name || "Guest"}
                  </div>
                  <div style={{ fontSize: "0.8rem", color: "var(--color-muted)" }}>
                    Pass: <strong>{confirmModalData.pass_code}</strong> •{" "}
                    {confirmModalData.mobile_number || "No Phone"}
                  </div>
                </div>
              </div>

              <div
                style={{
                  fontSize: "0.85rem",
                  color: "var(--color-text-secondary)",
                  lineHeight: 1.5,
                }}
              >
                Confirm that the guest is physically present at the gate terminal.
                Executing check-in will update the pass status and notify the host.
              </div>
            </div>
          )}
        </Modal>
      </div>
    </AdminLayout>
  );
};

export default GuardExpectedVisitors;
